var _templateObject, _templateObject2;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _taggedTemplateLiteralLoose(strings, raw) { if (!raw) { raw = strings.slice(0); } strings.raw = raw; return strings; }
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { AnimatePresence, motion } from 'framer-motion';
import { makeStyles } from '@material-ui/core/styles';
import ReactCrop from 'react-image-crop';
import DescriptionIcon from '@material-ui/icons/Description';
import DeleteIcon from '@material-ui/icons/Delete';
import ActionArea from '../ActionArea';
import Box from '../Box';
import CircularProgress from '../CircularProgress';
import Confirmation from '../Confirmation';
import DropZone from '../DropZone';
import FormControl from '../FormControl';
import FileUploadButton from '../FileUploadButton';
import FileViewer from '../FileViewer';
import Grid from '../Grid';
import Heading from '../Heading';
import IconButton from '../IconButton';
import Modal from '../Modal';
import ModalHeader from '../ModalHeader';
import ModalContent from '../ModalContent';
import CroppedImageInputActions from './CroppedImageInputActions';
import { isImage } from '../../utils/helpers';
import 'react-image-crop/dist/ReactCrop.css';
var _borderRadius = function borderRadius(edges, theme) {
  switch (edges) {
    case 'rounded':
      return 99;
    case 'sharp':
      return 0;
    default:
      return theme.shape.borderRadius;
  }
};
var useStyles = makeStyles(function (theme) {
  return {
    fileIcon: {
      color: theme.palette.default.light,
      margin: function margin(_ref) {
        var compact = _ref.compact;
        return compact ? "0 " + theme.spacing(2) + " 0 0" : '0 auto';
      },
      fontSize: function fontSize(_ref2) {
        var compact = _ref2.compact;
        return compact ? '2rem' : '4rem';
      }
    },
    savedImage: {
      borderRadius: function borderRadius(_ref3) {
        var edges = _ref3.edges;
        return _borderRadius(edges, theme);
      },
      border: "1px solid " + theme.palette.text.secondary,
      padding: theme.spacing(6, 4),
      '& img': {
        maxWidth: '100%',
        width: 'auto',
        hieght: 'auto',
        // maxHeight: '50vh',
        margin: '0 auto',
        boxShadow: theme.shadows[10],
        display: 'block'
      }
    },
    editor: {
      width: '100%',
      padding: theme.spacing(6, 4),
      backgroundImage: "linear-gradient(45deg, " + theme.palette.grey[700] + " 25%, transparent 25%), linear-gradient(-45deg, " + theme.palette.grey[700] + " 25%, transparent 25%), linear-gradient(45deg, transparent 75%, " + theme.palette.grey[700] + " 75%), linear-gradient(-45deg, transparent 75%, " + theme.palette.grey[700] + " 75%)",
      backgroundSize: '20px 20px',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px',
      border: function border(_ref4) {
        var showError = _ref4.showError;
        return "1px solid " + (showError ? theme.palette.error.main : theme.palette.primary.main);
      },
      borderRadius: function borderRadius(_ref5) {
        var edges = _ref5.edges;
        return _borderRadius(edges, theme);
      },
      '& .ReactCrop': {
        margin: '0 auto',
        display: 'block'
      },
      '& .ReactCrop__crop-selection': {}
    },
    loading: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -20,
      marginLeft: -20,
      zIndex: 1
    },
    success: {
      '& svg': {
        fontSize: 150,
        display: 'block',
        margin: '0 auto',
        color: theme.palette.success.main
      },
      '& *': {
        background: 'transparent'
      },
      '& .MuiPaper-root': {
        background: 'rgba(255, 255, 255, 0.7)'
      }
    }
  };
});
var StyledDeleteIcon = styled.span(_templateObject || (_templateObject = _taggedTemplateLiteralLoose(["\n  ", "\n"])), function (_ref6) {
  var theme = _ref6.theme;
  return css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteralLoose(["\n    display: inline-flex;\n    justify-content: center;\n    align-items: center;\n    position: absolute;\n    top: 50%;\n    right: 25px;\n    z-index: 9;\n    background: transparent;\n    color: ", ";\n    width: 48px;\n    height: 48px;\n    font-size: 20px;\n    transition: all 0.25s ease-in-out;\n    transform: translateY(-50%) scale(0.9);\n    box-shadow: ", ";\n    border-radius: 50%;\n    &:hover {\n      background: rgba(0, 0, 0, 0.04);\n      transform: translateY(-50%) scale(1.05);\n      color: ", ";\n      box-shadow: ", ";\n    }\n  "])), theme.palette.grey.main, theme.shadows[2], theme.palette.secondary.main, theme.shadows[4]);
});
function CroppedImageInput(_ref7) {
  var defaultValue = _ref7.defaultValue,
    disabled = _ref7.disabled,
    dropzoneLabel = _ref7.dropzoneLabel,
    dropzoneDescription = _ref7.dropzoneDescription,
    helperText = _ref7.helperText,
    helperTextPlacement = _ref7.helperTextPlacement,
    croppedImageQuality = _ref7.croppedImageQuality,
    label = _ref7.label,
    required = _ref7.required,
    showError = _ref7.showError,
    errorMessage = _ref7.errorMessage,
    edges = _ref7.edges,
    cropRatio = _ref7.cropRatio,
    inputProps = _ref7.inputProps,
    dropzoneProps = _ref7.dropzoneProps,
    onCancel = _ref7.onCancel,
    onSave = _ref7.onSave,
    onDelete = _ref7.onDelete,
    name = _ref7.name,
    dropppedFile = _ref7.dropppedFile,
    cropGuide = _ref7.cropGuide,
    showActions = _ref7.showActions,
    saveButtonProps = _ref7.saveButtonProps,
    cancelButtonProps = _ref7.cancelButtonProps,
    openInDialog = _ref7.openInDialog,
    fileViewerProps = _ref7.fileViewerProps;
  var _useState = useState(null),
    src = _useState[0],
    setSrc = _useState[1];
  var _useState2 = useState(''),
    filetype = _useState2[0],
    setFiletype = _useState2[1];
  var _useState3 = useState(null),
    originalImage = _useState3[0],
    setOriginalImage = _useState3[1];
  var _useState4 = useState(null),
    croppedImage = _useState4[0],
    setCroppedImage = _useState4[1];
  var _useState5 = useState(null),
    documentFile = _useState5[0],
    setDocumentFile = _useState5[1];
  var _useState6 = useState(false),
    confirmDelete = _useState6[0],
    setConfirmDelete = _useState6[1];
  var _useState7 = useState(false),
    viewDocument = _useState7[0],
    setViewDocument = _useState7[1];
  // const [editorWidth, setEditorWidth] = useState('100%');
  var _useState8 = useState(false),
    loading = _useState8[0],
    setLoading = _useState8[1];
  var _useState9 = useState(false),
    saving = _useState9[0],
    setSaving = _useState9[1];
  var _useState10 = useState(false),
    cancelling = _useState10[0],
    setCancelling = _useState10[1];
  var _useState11 = useState({
      aspect: cropRatio,
      unit: 'px'
    }),
    crop = _useState11[0],
    setCrop = _useState11[1];
  var input = useRef(null);
  var classes = useStyles({
    edges: edges,
    disabled: disabled,
    showError: showError
  });
  var isNotMobile = useMediaQuery(function (theme) {
    return theme.breakpoints.up('md');
  });
  useEffect(function () {
    if (defaultValue) {
      if (isImage(defaultValue)) {
        setCroppedImage(defaultValue);
      } else {
        setDocumentFile(defaultValue);
      }
    }
  }, [defaultValue]);
  var onDrop = useCallback(function (acceptedFiles) {
    setFiletype(acceptedFiles[0].type);
    if (!acceptedFiles[0].type.startsWith('image')) {
      setSaving(true);
    }
    setLoading(true);
    var reader = new FileReader();
    reader.addEventListener('load', function () {
      if (acceptedFiles[0].type.startsWith('image')) {
        setSrc(reader.result);
      } else {
        setDocumentFile(reader.result);
        onSave(reader.result, acceptedFiles[0]).then(function () {
          setSaving(false);
        });
      }
      setLoading(false);
    });
    reader.readAsDataURL(acceptedFiles[0]);
  }, [onSave]);
  useEffect(function () {
    if (dropppedFile) {
      onDrop(dropppedFile);
    }
  }, [dropppedFile, onDrop]);
  var handleCancel = function handleCancel() {
    setCancelling(true);
    onCancel().then(function () {
      setSrc(null);
      setOriginalImage(null);
      setCroppedImage(null);
      setConfirmDelete(false);
      setCancelling(false);
    });
  };
  var handleDelete = function handleDelete() {
    setCancelling(true);
    onDelete().then(function () {
      setSrc(null);
      setDocumentFile(null);
      setOriginalImage(null);
      setCroppedImage(null);
      setConfirmDelete(false);
      setCancelling(false);
    });
  };
  var fade = {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    }
  };
  var handleCropChange = function handleCropChange(newCrop) {
    return setCrop(newCrop);
  };
  var handleImageLoaded = function handleImageLoaded(image) {
    var width = image.width;
    if (window.innerHeight * 0.5 < image.height) {
      width = image.width * (window.innerHeight * 0.5 / image.height);
    }
    // if (width < window.innerWidth * 0.5) {
    //   setEditorWidth(`${width}px`);
    // }

    var height = image.height * (width / image.width);
    if (cropRatio) {
      var widthPercent = image.width / crop.aspect < image.height * crop.aspect ? 1 : image.height * crop.aspect / image.width;
      var heightPercent = image.width / crop.aspect > image.height * crop.aspect ? 1 : image.width / crop.aspect / image.height;
      var y = (1 - heightPercent) / 2;
      var x = (1 - widthPercent) / 2;
      setCrop({
        unit: 'px',
        width: width * widthPercent,
        height: height * heightPercent,
        x: width * x,
        y: height * y,
        aspect: crop.aspect
      });
    } else {
      setCrop({
        unit: 'px',
        width: width,
        height: height,
        x: 0,
        y: 0
      });
    }
    setOriginalImage(image);
    return false;
  };
  var handleImageSave = function handleImageSave() {
    if (originalImage && crop.width && crop.height) {
      var canvas = document.createElement('canvas');
      var scaleX = originalImage.naturalWidth / originalImage.width;
      var scaleY = originalImage.naturalHeight / originalImage.height;
      var ctx = canvas.getContext('2d');
      canvas.width = Math.floor(crop.width * scaleX);
      canvas.height = Math.floor(crop.height * scaleY);
      ctx.imageSmoothingQuality = 'high';
      var cropX = crop.x * scaleX;
      var cropY = crop.y * scaleY;
      var centerX = originalImage.naturalWidth / 2;
      var centerY = originalImage.naturalHeight / 2;
      ctx.save();
      // 5) Move the crop origin to the canvas origin (0,0)
      ctx.translate(-cropX, -cropY);
      // 4) Move the origin to the center of the original position
      ctx.translate(centerX, centerY);
      // 1) Move the center of the image to the origin (0,0)
      ctx.translate(-centerX, -centerY);
      ctx.drawImage(originalImage, 0, 0, originalImage.naturalWidth, originalImage.naturalHeight, 0, 0, originalImage.naturalWidth, originalImage.naturalHeight);
      ctx.restore();
      return new Promise(function (resolve, reject) {
        canvas.toBlob(function (blob) {
          if (!blob) {
            // eslint-disable-next-line no-console
            console.error('Canvas is empty');
            return;
          }
          setSaving(true);
          var reader = new FileReader();
          reader.addEventListener('load', function () {
            input.current.value = reader.result;
            onSave(reader.result, blob).then(function () {
              setCroppedImage(reader.result);
              setSaving(false);
            })
            // eslint-disable-next-line no-console
            .catch(function (e) {
              return console.error(e);
            });
          });
          reader.readAsDataURL(blob);
        }, 'image/jpeg', croppedImageQuality);
      });
    }
    return false;
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FileViewer, _extends({}, fileViewerProps, {
    file: {
      url: documentFile,
      filetype: filetype
    },
    open: viewDocument,
    onClose: function onClose() {
      return setViewDocument(false);
    }
  })), /*#__PURE__*/React.createElement(FormControl, {
    key: "form-control",
    name: name,
    fullWidth: true,
    label: label,
    helperText: helperText,
    helperTextPlacement: helperTextPlacement,
    disabled: disabled,
    required: required,
    showError: showError,
    errorMessage: errorMessage,
    edges: edges
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("input", _extends({
    type: "hidden",
    ref: input
  }, inputProps, {
    key: "hidden-input"
  })), saving || cancelling && /*#__PURE__*/React.createElement(CircularProgress, {
    className: classes.loading,
    key: "loading"
  }), /*#__PURE__*/React.createElement(AnimatePresence, {
    exitBeforeEnter: true
  }, (croppedImage || documentFile) && !showError && /*#__PURE__*/React.createElement(motion.div, _extends({}, fade, {
    className: classes.savedImage,
    key: "saved-image"
  }), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    alignItems: "center",
    spacing: 5
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    md: 11
  }, croppedImage && /*#__PURE__*/React.createElement("img", {
    src: croppedImage,
    alt: ""
  }), documentFile && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    md: 1,
    style: {
      justifyContent: 'center',
      alignItems: 'center'
    }
  }, /*#__PURE__*/React.createElement(IconButton, {
    onClick: function onClick() {
      return setViewDocument(true);
    }
  }, /*#__PURE__*/React.createElement(DescriptionIcon, {
    className: classes.fileIcon
  })))), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    md: 1
  }, /*#__PURE__*/React.createElement(ActionArea, {
    onClick: function onClick() {
      return setConfirmDelete(true);
    }
  }, /*#__PURE__*/React.createElement(StyledDeleteIcon, null, /*#__PURE__*/React.createElement(DeleteIcon, null)))))), !src && !documentFile && !croppedImage && (openInDialog ? /*#__PURE__*/React.createElement(motion.div, _extends({}, fade, {
    key: "upload-button"
  }), /*#__PURE__*/React.createElement(FileUploadButton, {
    onUpload: onDrop,
    fullWidth: !isNotMobile
  }, dropzoneLabel)) : /*#__PURE__*/React.createElement(motion.div, _extends({}, fade, {
    key: "dropzone"
  }), /*#__PURE__*/React.createElement(DropZone, {
    loading: loading,
    disabled: disabled,
    onDrop: onDrop,
    edges: edges,
    dropzoneLabel: dropzoneLabel,
    dropzoneDescription: dropzoneDescription,
    dropzoneProps: _extends({
      accept: ['image/*']
    }, dropzoneProps, {
      maxFiles: 1
    })
  }))), src !== null && documentFile === null && (!croppedImage || showError) && (openInDialog ? /*#__PURE__*/React.createElement(Modal, {
    open: true,
    fullWidth: true,
    fullScreen: true
  }, /*#__PURE__*/React.createElement(ModalHeader, {
    onClose: handleCancel
  }, /*#__PURE__*/React.createElement(Heading, {
    as: "h4"
  }, "Crop Image")), /*#__PURE__*/React.createElement(ModalContent, null, /*#__PURE__*/React.createElement(Box, {
    pl: 2.5,
    pr: 2.5,
    pb: 2.5
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.editor
  }, /*#__PURE__*/React.createElement(ReactCrop, {
    src: src,
    crop: crop,
    style: {
      width: '100%'
    },
    onChange: handleCropChange,
    onImageLoaded: handleImageLoaded,
    renderSelectionAddon: cropGuide
  })), /*#__PURE__*/React.createElement(CroppedImageInputActions, {
    handleCancel: handleCancel,
    cancelling: cancelling,
    cancelButtonProps: cancelButtonProps,
    handleImageSave: handleImageSave,
    saving: saving,
    saveButtonProps: saveButtonProps,
    disabled: disabled
  })))) : /*#__PURE__*/React.createElement(motion.div, _extends({}, fade, {
    key: "editor"
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.editor
  }, /*#__PURE__*/React.createElement(ReactCrop, {
    src: src,
    crop: crop,
    style: {
      width: '100%'
    },
    onChange: handleCropChange,
    onImageLoaded: handleImageLoaded,
    renderSelectionAddon: cropGuide
  }))))))), (!openInDialog && src !== null && (!croppedImage || showError) || !openInDialog && showActions) && /*#__PURE__*/React.createElement(CroppedImageInputActions, {
    handleCancel: handleCancel,
    cancelling: cancelling,
    cancelButtonProps: cancelButtonProps,
    handleImageSave: handleImageSave,
    saving: saving,
    saveButtonProps: saveButtonProps,
    disabled: disabled
  }), /*#__PURE__*/React.createElement(Confirmation, {
    open: confirmDelete,
    onCancel: function onCancel() {
      return setConfirmDelete(false);
    },
    onConfirm: handleDelete,
    message: "Are you sure you want to remove the attached image?"
  }));
}
CroppedImageInput.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * The image quality of cropped
   */
  croppedImageQuality: PropTypes.number,
  /**
   * URL for an existing image already uploaded to this field
   */
  defaultValue: PropTypes.string,
  /**
   * If `true`, the label, input and helper text should be displayed in a disabled state.
   */
  disabled: PropTypes.bool,
  /**
   * Text label displayed in the dropzone
   */
  dropzoneLabel: PropTypes.string,
  /**
   * Text description displayed in the dropzone
   */
  dropzoneDescription: PropTypes.string,
  /**
   * Pass props for react-dropzone. See https://react-dropzone.js.org/
   */
  dropzoneProps: PropTypes.object,
  /**
   * Whether or not to display an error message (if there is a message to display).
   */
  showError: PropTypes.bool,
  /**
   * The error message to display if showError === true
   */
  errorMessage: PropTypes.string,
  /**
   * The helper text content.
   */
  helperText: PropTypes.node,
  /**
   * Placed the helper text above or below the input.
   */
  helperTextPlacement: PropTypes.oneOf(['above', 'below']),
  /**
   * Ration crop selection e.g. 16 / 9
   */
  cropRatio: PropTypes.number,
  /**
   * Do something when user saves cropped image, received the base64 string
   */
  onSave: PropTypes.func,
  /**
   * Do something when user removes image
   */
  onDelete: PropTypes.func,
  /**
   * Do something when user requests cancel action
   */
  onCancel: PropTypes.func,
  /**
   * Pass through props for hidden input that holds the data url of the cropped image
   */
  inputProps: PropTypes.object,
  /**
   * The label content.
   */
  label: PropTypes.string,
  /**
   * The field name
   */
  name: PropTypes.string.isRequired,
  /**
   * If `true`, the label will indicate that the input is required.
   */
  required: PropTypes.bool,
  /**
   * Make button edges rounded or square
   */
  edges: PropTypes.oneOf(['normal', 'rounded', 'sharp']),
  /**
   * Result from dropzone...
   */
  dropppedFile: PropTypes.any,
  /**
   * @ignore
   */
  cropGuide: PropTypes.any,
  /**
   * If `true`, always show cancel and save button
   */
  showActions: PropTypes.bool,
  /**
   * Props for save button
   */
  saveButtonProps: PropTypes.object,
  /**
   * Props for cancel button
   */
  cancelButtonProps: PropTypes.object,
  /**
   * Replace the dropzone with a button that opens a dialog
   */
  openInDialog: PropTypes.bool,
  /**
   * Fileviewer props
   */
  fileViewerProps: PropTypes.object
} : {};
CroppedImageInput.defaultProps = {
  croppedImageQuality: 0.6,
  disabled: false,
  defaultValue: null,
  dropzoneLabel: 'Upload file',
  dropzoneDescription: 'Drag and drop a file here, or click to select a file',
  helperText: '',
  onSave: function onSave() {
    return Promise.resolve(true);
  },
  onDelete: function onDelete() {
    return Promise.resolve(true);
  },
  onCancel: function onCancel() {
    return Promise.resolve(true);
  },
  helperTextPlacement: 'above',
  label: '',
  required: false,
  showError: false,
  errorMessage: '',
  edges: 'normal',
  cropRatio: null,
  inputProps: null,
  dropzoneProps: null,
  dropppedFile: null,
  cropGuide: null,
  showActions: false,
  saveButtonProps: {},
  cancelButtonProps: {},
  openInDialog: false,
  fileViewerProps: {}
};
export default CroppedImageInput;